import { Check, X } from 'lucide-react';
import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

interface ToastItem {
  id: number;
  message: ReactNode;
  tone: 'success' | 'error';
  undo?: () => void | Promise<void>;
  until: number;
}

interface ToastApi {
  /**
   * `seconds` is how long the undo stays offered. A caller whose undo window is a
   * company setting (`settings.stock.undo_seconds`, which the confirmation sheets
   * quote) passes it, so the countdown and the promised window are one number.
   */
  success(message: ReactNode, opts?: { undo?: () => void | Promise<void>; seconds?: number }): void;
  error(message: ReactNode): void;
}

const Ctx = createContext<ToastApi | null>(null);
const UNDO_SECONDS = 30;

/** Toast (#1F1F1D surface, tick, "Geri al 28sn" gold undo). 30 s when undoable. */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const seq = useRef(0);
  const [, force] = useState(0);

  useEffect(() => {
    if (!items.length) return;
    const t = setInterval(() => {
      const now = Date.now();
      setItems((xs) => xs.filter((x) => x.until > now));
      force((n) => n + 1);
    }, 1000);
    return () => clearInterval(t);
  }, [items.length]);

  const push = useCallback((item: Omit<ToastItem, 'id' | 'until'>, seconds: number) => {
    seq.current += 1;
    const id = seq.current;
    setItems((xs) => [...xs.slice(-2), { ...item, id, until: Date.now() + seconds * 1000 }]);
  }, []);

  const api: ToastApi = {
    success: (message, opts) => push({ message, tone: 'success', undo: opts?.undo }, opts?.undo ? (opts.seconds ?? UNDO_SECONDS) : 5),
    error: (message) => push({ message, tone: 'error' }, 8),
  };

  return (
    <Ctx.Provider value={api}>
      {children}
      <div className="pointer-events-none fixed bottom-5 left-1/2 z-[60] flex w-[min(520px,calc(100vw-32px))] -translate-x-1/2 flex-col gap-2" aria-live="polite">
        {items.map((it) => (
          <ToastView key={it.id} item={it} onClose={() => setItems((xs) => xs.filter((x) => x.id !== it.id))} />
        ))}
      </div>
    </Ctx.Provider>
  );
}

function ToastView({ item, onClose }: { item: ToastItem; onClose(): void }) {
  const { t } = useTranslation();
  const left = Math.max(0, Math.ceil((item.until - Date.now()) / 1000));
  return (
    <div className="pointer-events-auto flex items-center gap-3 rounded-card bg-toast-surface px-4 py-3 text-[13px] text-white shadow-overlay" role="status">
      {item.tone === 'success' ? <Check className="size-4 flex-none text-[#8FD3AC]" /> : <X className="size-4 flex-none text-[#F0A79E]" />}
      <div className="min-w-0 flex-1">{item.message}</div>
      {item.undo && (
        <button
          type="button"
          className="font-semibold whitespace-nowrap text-[#C9A653] hover:underline"
          onClick={async () => {
            onClose();
            await item.undo?.();
          }}
        >
          {t('common.undoIn', { seconds: left })}
        </button>
      )}
      <button type="button" aria-label={t('common.close')} onClick={onClose} className="text-white/60 hover:text-white">
        <X className="size-4" />
      </button>
    </div>
  );
}

export function useToast(): ToastApi {
  const v = useContext(Ctx);
  if (!v) throw new Error('useToast outside ToastProvider');
  return v;
}
